import { useTranslation } from '@doccareplus/i18n';
import { detailSlots, resolveLabel } from './format';
import { PrescriptionDocument } from './PrescriptionDocument';
import type {
  PrescriptionBrandingResponse,
  PrescriptionDocumentData,
  PrescriptionFormatResponse,
} from './types';

/**
 * Configuration preview data adapter. It supplies representative clinical values to the same PrescriptionDocument
 * component used by real preview and print surfaces; it does not maintain a second document layout.
 */
export function PrescriptionSamplePaper({
  branding,
  format,
  providerName,
  providerQualification,
  providerRegNo,
}: {
  branding: PrescriptionBrandingResponse | undefined;
  format: PrescriptionFormatResponse | undefined;
  providerName?: string;
  providerQualification?: string | null;
  providerRegNo?: string | null;
}) {
  const { t } = useTranslation('appointments');
  const { t: ts } = useTranslation('settings');
  const slots = detailSlots(format);
  const sampleFor = (slotKey: string, fallback: string) =>
    format?.slots.find((slot) => slot.key === slotKey)?.vocabulary[0] ?? fallback;

  const document: PrescriptionDocumentData = {
    provider: {
      name: providerName ?? '',
      qualification: providerQualification,
      registrationNumber: providerRegNo,
    },
    patient: {
      patientNumber: ts('branding.samplePatientNumber'),
      fullName: ts('branding.samplePatientName'),
      age: 34,
      gender: null,
    },
    visitDate: new Date().toISOString(),
    vitals: [ts('branding.sampleVitals', { defaultValue: 'BP 120/80' })],
    complaint: ts('branding.sampleContent'),
    diagnosis: ts('branding.sampleContent'),
    investigations: [],
    items: [{
      medicineName: sampleFor('MedicineName', ts('branding.sampleMedicine')),
      dose: sampleFor('Dose', resolveSlotLabel('Dose')),
      frequency: sampleFor('Frequency', resolveSlotLabel('Frequency')),
      duration: sampleFor('Duration', resolveSlotLabel('Duration')),
      instructions: sampleFor('Instructions', resolveSlotLabel('Instructions')),
      order: 1,
    }],
    advice: ts('branding.sampleContent'),
    followUpDate: new Date().toISOString(),
  };

  return <PrescriptionDocument branding={branding} format={format} document={document} />;

  function resolveSlotLabel(key: string) {
    const slot = slots.find((candidate) => candidate.key === key);
    return slot ? resolveLabel(slot.labelKey, slot.labelOverride, t) : '';
  }
}
